/**
 * Lawn care → This week: every treatment round, old-job visit and add-on due
 * in the next days, one list by day, with the mow each one rides. Pure: it
 * reads what GET /treatments/this-week and GET /mow-addons/overview return
 * and lays it out; nothing here calls the server. (server/tests/lawn-care-page
 * .test.js compiles this file and checks the laying out.)
 */
export type WeekItem = {
  kind: 'round' | 'legacy' | 'addon';
  d: string;
  customer_id: number | null;
  customer_name: string;
  address: string | null;
  /** What is being done: the round's label, the old job's service, the add-on's name. */
  label: string;
  /** Where it sits: "on the mow", "a visit of its own", or waiting for a mow that is not on the calendar yet. */
  where: string;
  rides: boolean;
  job_id: number | null;
  who: string | null;
  /** What it bills, in words — a plan visit bills nothing of its own. */
  price_words: string;
  /** Put there by a schedule's standing rule (add-ons only). */
  by_rule?: boolean;
  billed?: boolean;
};

export type WeekDay = { d: string; items: WeekItem[] };

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const money = (n: any) => `$${Number(n || 0).toFixed(2)}`;

export function isoPlus(dateISO: string, days: number): string {
  const d = new Date(dateISO.slice(0, 10) + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** One round of a plan (or a step program) as the week shows it. */
export function roundItem(r: any): WeekItem {
  const rides = !!r.rides;
  return {
    kind: 'round', d: String(r.d || '').slice(0, 10), customer_id: r.customer_id ?? null, customer_name: String(r.customer_name || ''),
    address: r.address || null, label: `${r.plan_name ? `${r.plan_name}: ` : ''}${r.label || 'treatment'}`,
    where: rides ? `on the mow${r.mow_date && r.mow_date !== String(r.d || '').slice(0, 10) ? ` of ${r.mow_date}` : ''}` : 'a visit of its own',
    rides, job_id: r.job_id ?? null, who: r.who || null,
    price_words: r.sheet_plan ? (r.monthly_price ? `on the plan, ${money(r.monthly_price)} a month` : 'on the plan') : (r.round_price ? money(r.round_price) : 'no price'),
  };
}

/** A visit of the old every-4-weeks job. Never on a mow: it is a job of its own. */
export function legacyItem(l: any): WeekItem {
  return {
    kind: 'legacy', d: String(l.d || '').slice(0, 10), customer_id: l.customer_id ?? null, customer_name: String(l.customer_name || ''),
    address: l.address || null, label: `Old job: ${l.label || String(l.service_type || 'treatment').replace(/_/g, ' ')}`,
    where: 'a visit of its own', rides: false, job_id: l.job_id ?? null, who: l.who || null,
    price_words: l.price ? `${money(l.price)} a visit` : 'no price',
  };
}

/** An add-on on a visit. It rides the visit it sits on; on a mow that is the mow. */
export function addonItem(u: any): WeekItem {
  return {
    kind: 'addon', d: String(u.d || '').slice(0, 10), customer_id: u.customer_id ?? null, customer_name: String(u.customer_name || ''),
    address: u.address || null, label: `Add-on: ${u.name || u.addon_key || 'extra'}`,
    where: u.rides ? 'on the mow' : 'on a visit of its own', rides: !!u.rides, job_id: u.job_id ?? null, who: u.who || null,
    price_words: `${money(u.price)}${u.minutes ? ` · ${u.minutes} min` : ''}`, by_rule: !!u.standing, billed: !!u.invoiced_at,
  };
}

/**
 * The days from `from`, `days` of them, each with what is due that day in one list — rounds first, then the old
 * job's visits, then add-ons, by customer within each. A day with nothing is still a day (the page says so).
 * Anything dated outside the window is left out, so two reads with different windows cannot pad the week.
 */
export function groupWeek(from: string, days: number, data: { rounds?: any[]; legacy?: any[]; upcoming?: any[] }): WeekDay[] {
  const start = ISO.test(String(from || '')) ? String(from) : '';
  const n = Math.max(1, Math.min(31, Math.round(Number(days)) || 7));
  if (!start) return [];
  const end = isoPlus(start, n - 1);
  const order = { round: 0, legacy: 1, addon: 2 } as const;
  const items: WeekItem[] = [
    ...(data.rounds || []).map(roundItem),
    ...(data.legacy || []).map(legacyItem),
    ...(data.upcoming || []).map(addonItem),
  ].filter(i => ISO.test(i.d) && i.d >= start && i.d <= end);
  items.sort((a, b) => (a.d < b.d ? -1 : a.d > b.d ? 1 : 0) || order[a.kind] - order[b.kind] || a.customer_name.localeCompare(b.customer_name) || a.label.localeCompare(b.label));
  const out: WeekDay[] = [];
  for (let k = 0; k < n; k++) {
    const d = isoPlus(start, k);
    out.push({ d, items: items.filter(i => i.d === d) });
  }
  return out;
}

/** "Mon, Nov 2" — and "Today" / "Tomorrow" when it is. */
export function dayHeading(d: string, today: string): string {
  const words = new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  if (d === today) return `Today · ${words}`;
  if (d === isoPlus(today, 1)) return `Tomorrow · ${words}`;
  return words;
}
